import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import api from "../api/axios";
import { parseCfg, MiniResultCard, FullResultCard } from "../components/ResultCardPreview";

const TABS = [
  { key: "overview",     label: "Overview" },
  { key: "schools",      label: "Schools" },
  { key: "teachers",     label: "Teachers" },
  { key: "students",     label: "Students" },
  { key: "plans",        label: "Subscription Plans" },
  { key: "templates",    label: "Result Templates" },
  { key: "gateways",     label: "Gateway Settings" },
  { key: "superadmins",  label: "Super Admins" },
];

const VIEW_META = {
  one_exam:   { cols: 1, label: "One-Exam View" },
  two_exam:   { cols: 2, label: "Two-Exam View" },
  three_exam: { cols: 3, label: "Three-Exam View" },
  four_exam:  { cols: 4, label: "Four-Exam View" },
};

const SA_VALID_TABS = new Set(TABS.map((t) => t.key));

export default function SuperAdminDashboard() {
  const [tab, setTab] = useState(() => {
    const saved = sessionStorage.getItem("spa_tab");
    return saved && SA_VALID_TABS.has(saved) ? saved : "overview";
  });

  function navigate(t) {
    sessionStorage.setItem("spa_tab", t);
    setTab(t);
  }

  return (
    <div className="app-shell">
      <Sidebar title="Super Admin" tabs={TABS} activeTab={tab} onTabChange={navigate} />
      <div className="main-content">
        {tab === "overview"  && <Overview />}
        {tab === "schools"   && <Schools />}
        {tab === "teachers"  && <Teachers />}
        {tab === "students"  && <Students />}
        {tab === "plans"     && <Plans />}
        {tab === "templates"   && <Templates />}
        {tab === "gateways"    && <GatewaySettings />}
        {tab === "superadmins" && <SuperAdmins />}
      </div>
    </div>
  );
}

/* ======================================================
   Overview Tab
   ====================================================== */
function Overview() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/super-admin/dashboard")
      .then((r) => setSummary(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Platform Overview</h1>
        <p className="subtitle">All schools on Volcanic Result Manager</p>
      </div>

      {loading ? (
        <p className="text-muted">Loading stats...</p>
      ) : (
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-card-icon">🏫</div>
            <div className="stat-card-value">{summary?.activeSchools ?? 0}</div>
            <div className="stat-card-label">Active Schools</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon">📊</div>
            <div className="stat-card-value">{summary?.totalSchools ?? 0}</div>
            <div className="stat-card-label">Total Schools</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon">💰</div>
            <div className="stat-card-value">
              ₹{summary ? Number(summary.totalRevenue).toLocaleString("en-IN") : 0}
            </div>
            <div className="stat-card-label">Total Revenue</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon">👩‍🏫</div>
            <div className="stat-card-value">{summary?.totalTeachers ?? 0}</div>
            <div className="stat-card-label">Total Teachers</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon">🎒</div>
            <div className="stat-card-value">{summary?.totalStudents ?? 0}</div>
            <div className="stat-card-label">Total Students</div>
          </div>
        </div>
      )}

      <div className="card">
        <h3>Quick Actions</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn secondary" onClick={() => {}}>
            📥 Download Revenue Report
          </button>
          <a href="/api/health" target="_blank" rel="noreferrer" className="btn secondary">
            🔍 API Health Check
          </a>
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Schools Tab
   ====================================================== */
function Schools() {
  const [schools, setSchools] = useState([]);
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    schoolName: "", address: "", contactEmail: "", contactMobile: "",
    planId: "", adminName: "", adminUsername: "", adminPassword: "",
  });
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [detailsModal, setDetailsModal] = useState(null); // school details object
  const [detailsLoading, setDetailsLoading] = useState(false);

  // School details form state
  const [assignTplId, setAssignTplId] = useState("");
  const [assignMsg, setAssignMsg] = useState("");
  const [subPlanId, setSubPlanId] = useState("");
  const [subDays, setSubDays] = useState(365);
  const [subMsg, setSubMsg] = useState("");
  const [resetPwd, setResetPwd] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [resetErr, setResetErr] = useState("");
  const [portalCopied, setPortalCopied] = useState(false);

  function copyPortalLink(url) {
    navigator.clipboard.writeText(url).then(() => {
      setPortalCopied(true);
      setTimeout(() => setPortalCopied(false), 2000);
    });
  }

  // Edit school modal
  const [editSchool, setEditSchool] = useState(null);
  const [editSchoolForm, setEditSchoolForm] = useState({});
  const [editSchoolMsg, setEditSchoolMsg] = useState("");
  const [editSchoolSaving, setEditSchoolSaving] = useState(false);

  // Send password
  const [sendPwdModal, setSendPwdModal] = useState(null);
  const [sendPwdValue, setSendPwdValue] = useState("");
  const [sendPwdMsg, setSendPwdMsg] = useState("");
  const [sendPwdLoading, setSendPwdLoading] = useState(false);

  // School admins management
  const [schoolAdmins, setSchoolAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [addAdminForm, setAddAdminForm] = useState({ name: "", username: "", password: "", email: "", mobile: "" });
  const [addAdminMsg, setAddAdminMsg] = useState("");
  const [addAdminErr, setAddAdminErr] = useState("");
  const [addAdminSaving, setAddAdminSaving] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null); // { id, name, email, mobile, ... }
  const [editAdminForm, setEditAdminForm] = useState({});
  const [editAdminMsg, setEditAdminMsg] = useState("");
  const [editAdminSaving, setEditAdminSaving] = useState(false);
  const [editAdminCreds, setEditAdminCreds] = useState(null);
  const [editCredsForm, setEditCredsForm] = useState({ username: "", password: "" });
  const [editCredsMsg, setEditCredsMsg] = useState("");
  const [editCredsSaving, setEditCredsSaving] = useState(false);

  function load() {
    api.get("/super-admin/schools").then((r) => setSchools(r.data));
  }

  useEffect(load, []);
  useEffect(() => {
    api.get("/super-admin/plans").then((r) => setPlans(r.data));
    api.get("/super-admin/templates").then((r) => setTemplates(r.data));
  }, []);

  async function createSchool(e) {
    e.preventDefault();
    setFormError(""); setFormSuccess("");
    try {
      await api.post("/super-admin/schools", { ...form, planId: parseInt(form.planId, 10) });
      setForm({ schoolName: "", address: "", contactEmail: "", contactMobile: "", planId: "", adminName: "", adminUsername: "", adminPassword: "" });
      setFormSuccess("School created successfully!");
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to create school.");
    }
  }

  async function toggleStatus(school) {
    const status = school.status === "active" ? "suspended" : "active";
    await api.put(`/super-admin/schools/${school.id}/status`, { status });
    load();
  }

  async function openDetails(school) {
    setDetailsLoading(true);
    setAssignMsg(""); setSubMsg(""); setResetMsg(""); setResetErr("");
    setShowAddAdmin(false); setAddAdminForm({ name: "", username: "", password: "", email: "", mobile: "" });
    setAddAdminMsg(""); setAddAdminErr("");
    try {
      const r = await api.get(`/super-admin/schools/${school.id}/details`);
      setDetailsModal(r.data);
      setAssignTplId(r.data.templateSelection?.template_id || "");
      setSubPlanId(r.data.plan_id || "");
      setSubDays(365);
      setResetPwd("");
      loadSchoolAdmins(school.id);
    } catch {
      // fallback: use school object
      setDetailsModal(school);
      loadSchoolAdmins(school.id);
    } finally {
      setDetailsLoading(false);
    }
  }

  async function saveTemplate() {
    setAssignMsg("");
    try {
      await api.put(`/super-admin/schools/${detailsModal.id}/template`, { template_id: parseInt(assignTplId, 10) });
      setAssignMsg("Template assigned successfully.");
    } catch (err) {
      setAssignMsg(err.response?.data?.error || "Failed to assign template.");
    }
  }

  async function extendSub() {
    setSubMsg("");
    try {
      const r = await api.put(`/super-admin/schools/${detailsModal.id}/subscription`, { plan_id: subPlanId || undefined, days: subDays });
      setSubMsg(`Subscription extended until ${new Date(r.data.expires_at).toLocaleDateString("en-IN")}.`);
      load();
    } catch (err) {
      setSubMsg(err.response?.data?.error || "Failed to extend subscription.");
    }
  }

  async function doResetPwd() {
    setResetMsg(""); setResetErr("");
    try {
      const r = await api.put(`/super-admin/schools/${detailsModal.id}/reset-admin-password`, { new_password: resetPwd });
      setResetMsg(r.data.message);
      setResetPwd("");
    } catch (err) {
      setResetErr(err.response?.data?.error || "Failed to reset password.");
    }
  }

  function openEditSchool(school) {
    setEditSchool(school);
    setEditSchoolForm({
      name: school.name || "",
      address: school.address || "",
      contact_email: school.contact_email || "",
      contact_mobile: school.contact_mobile || "",
    });
    setEditSchoolMsg("");
  }

  async function saveEditSchool() {
    setEditSchoolSaving(true); setEditSchoolMsg("");
    try {
      await api.put(`/super-admin/schools/${editSchool.id}`, editSchoolForm);
      setEditSchoolMsg("School details updated.");
      load();
      setTimeout(() => setEditSchool(null), 800);
    } catch (err) {
      setEditSchoolMsg(err.response?.data?.error || "Failed to update.");
    } finally {
      setEditSchoolSaving(false);
    }
  }

  function openSendPwd(school) {
    setSendPwdModal(school);
    setSendPwdValue("");
    setSendPwdMsg("");
  }

  async function doSendPassword() {
    setSendPwdLoading(true); setSendPwdMsg("");
    try {
      const r = await api.post(`/super-admin/schools/${sendPwdModal.id}/send-admin-password`, {
        password: sendPwdValue || undefined,
      });
      const emailStatus = r.data.email === "sent" ? "Email sent" : r.data.email === "failed" ? "Email failed" : "Email not configured";
      const smsStatus   = r.data.sms   === "sent" ? "SMS sent"   : r.data.sms   === "failed" ? "SMS failed"   : "SMS not configured";
      setSendPwdMsg(`Done. ${emailStatus}. ${smsStatus}. Password: ${r.data.password}`);
    } catch (err) {
      setSendPwdMsg(err.response?.data?.error || "Failed to send.");
    } finally {
      setSendPwdLoading(false);
    }
  }

  function loadSchoolAdmins(schoolId) {
    setAdminsLoading(true);
    api.get(`/super-admin/schools/${schoolId}/admins`)
      .then((r) => setSchoolAdmins(r.data))
      .catch(() => setSchoolAdmins([]))
      .finally(() => setAdminsLoading(false));
  }

  async function submitAddAdmin(e) {
    e.preventDefault();
    setAddAdminSaving(true); setAddAdminMsg(""); setAddAdminErr("");
    try {
      await api.post(`/super-admin/schools/${detailsModal.id}/admins`, addAdminForm);
      setAddAdminMsg("Admin account created.");
      setAddAdminForm({ name: "", username: "", password: "", email: "", mobile: "" });
      setShowAddAdmin(false);
      loadSchoolAdmins(detailsModal.id);
    } catch (err) {
      setAddAdminErr(err.response?.data?.error || "Failed to create admin.");
    } finally {
      setAddAdminSaving(false);
    }
  }

  function openEditAdmin(admin) {
    setEditAdmin(admin);
    setEditAdminForm({ name: admin.name || "", username: admin.username || "", email: admin.email || "", mobile: admin.mobile || "" });
    setEditAdminMsg("");
  }

  async function saveEditAdmin() {
    setEditAdminSaving(true); setEditAdminMsg("");
    try {
      await api.put(`/super-admin/schools/${detailsModal.id}/admins/${editAdmin.id}`, editAdminForm);
      setEditAdminMsg("Saved.");
      loadSchoolAdmins(detailsModal.id);
      setTimeout(() => setEditAdmin(null), 700);
    } catch (err) {
      setEditAdminMsg(err.response?.data?.error || "Failed to save.");
    } finally {
      setEditAdminSaving(false);
    }
  }

  function openEditCreds(admin) {
    setEditAdminCreds(admin);
    setEditCredsForm({ username: admin.username || "", password: "" });
    setEditCredsMsg("");
  }

  async function saveEditCreds() {
    setEditCredsSaving(true); setEditCredsMsg("");
    try {
      await api.put(`/super-admin/schools/${detailsModal.id}/admins/${editAdminCreds.id}/credentials`, editCredsForm);
      setEditCredsMsg("Credentials updated.");
      loadSchoolAdmins(detailsModal.id);
      setTimeout(() => setEditAdminCreds(null), 700);
    } catch (err) {
      setEditCredsMsg(err.response?.data?.error || "Failed to update.");
    } finally {
      setEditCredsSaving(false);
    }
  }

  async function toggleAdminStatus(admin) {
    try {
      await api.put(`/super-admin/schools/${detailsModal.id}/admins/${admin.id}/toggle-status`);
      loadSchoolAdmins(detailsModal.id);
    } catch { /* ignore */ }
  }

  function statusBadge(status) {
    const map = { active: "active", suspended: "suspended", pending_payment: "pending" };
    return <span className={`badge ${map[status] || "info"}`}>{status}</span>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Schools (Tenants)</h1>
        <p className="subtitle">Onboard and manage all school accounts on this platform.</p>
      </div>

      <div className="card">
        <h3>Onboard a School Directly</h3>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          Use this to manually add a school (skips self-registration flow).
        </p>
        {formError && <div className="error-text" style={{ marginBottom: 12 }}>{formError}</div>}
        {formSuccess && <div className="success-text" style={{ marginBottom: 12 }}>{formSuccess}</div>}
        <form onSubmit={createSchool}>
          <div className="form-section-title">School Information</div>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">School Name *</label>
              <input placeholder="e.g. St. Mary's High School" value={form.schoolName} onChange={(e) => setForm({ ...form, schoolName: e.target.value })} required />
            </div>
            <div className="form-field">
              <label className="form-label">Address</label>
              <input placeholder="School address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Contact Email</label>
              <input type="email" placeholder="school@example.com" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Contact Mobile</label>
              <input placeholder="Mobile number" value={form.contactMobile} onChange={(e) => setForm({ ...form, contactMobile: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Subscription Plan *</label>
              <select value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })} required>
                <option value="">-- Select Plan --</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ₹{p.price}</option>)}
              </select>
            </div>
          </div>
          <div className="form-section-title">Admin Account</div>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Admin Name *</label>
              <input placeholder="Principal / Admin name" value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} required />
            </div>
            <div className="form-field">
              <label className="form-label">Admin Username *</label>
              <input placeholder="Login username" value={form.adminUsername} onChange={(e) => setForm({ ...form, adminUsername: e.target.value })} required />
            </div>
            <div className="form-field">
              <label className="form-label">Temporary Password *</label>
              <input type="password" placeholder="Min 8 characters" value={form.adminPassword} onChange={(e) => setForm({ ...form, adminPassword: e.target.value })} required />
            </div>
          </div>
          <button className="btn" type="submit">Create School + Admin</button>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0 }}>All Schools</h3>
          <span className="badge info">{schools.length} total</span>
        </div>
        {schools.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏫</div>
            <div className="empty-state-text">No schools yet. Onboard the first one above.</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>School Name</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Expires</th>
                <th>Teachers</th>
                <th>Students</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td>
                  <td>{statusBadge(s.status)}</td>
                  <td>{s.Plan?.name || <span className="text-muted">—</span>}</td>
                  <td>
                    {s.subscription_expires_at
                      ? new Date(s.subscription_expires_at).toLocaleDateString("en-IN")
                      : <span className="text-muted">—</span>}
                  </td>
                  <td>{s.teacherCount ?? <span className="text-muted">—</span>}</td>
                  <td>{s.studentCount ?? <span className="text-muted">—</span>}</td>
                  <td style={{ fontSize: 13, color: "var(--muted)" }}>{s.contact_email || "—"}</td>
                  <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button
                      className={`btn sm ${s.status === "active" ? "danger" : "success"}`}
                      onClick={() => toggleStatus(s)}
                    >
                      {s.status === "active" ? "Suspend" : "Activate"}
                    </button>
                    <button className="btn sm secondary" onClick={() => openDetails(s)}>Details</button>
                    <button className="btn sm secondary" onClick={() => openEditSchool(s)}>Edit</button>
                    <button className="btn sm secondary" onClick={() => openSendPwd(s)}>Send Password</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* School Details Modal */}
      {detailsModal && (
        <div className="tpl-modal-overlay" onClick={() => setDetailsModal(null)}>
          <div className="tpl-modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div>
                <strong style={{ fontSize: 16 }}>{detailsModal.name}</strong>
                <span className="text-muted" style={{ marginLeft: 10, fontSize: 13 }}>School Details &amp; Management</span>
              </div>
              <button className="tpl-modal-close" onClick={() => setDetailsModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {detailsLoading ? (
                <p className="text-muted">Loading details...</p>
              ) : (
                <>
                  {/* School Info */}
                  <div className="form-section-title">School Information</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Address</span><div>{detailsModal.address || "—"}</div></div>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Contact Email</span><div>{detailsModal.contact_email || "—"}</div></div>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Mobile</span><div>{detailsModal.contact_mobile || "—"}</div></div>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Status</span><div>{detailsModal.status}</div></div>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Current Plan</span><div>{detailsModal.Plan?.name || "—"}</div></div>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Expires</span><div>{detailsModal.subscription_expires_at ? new Date(detailsModal.subscription_expires_at).toLocaleDateString("en-IN") : "—"}</div></div>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Teachers</span><div>{detailsModal.teacherCount ?? "—"}</div></div>
                    <div><span className="text-muted" style={{ fontSize: 12 }}>Students</span><div>{detailsModal.studentCount ?? "—"}</div></div>
                  </div>

                  {/* Student Portal Link */}
                  {(() => {
                    const portalUrl = `${window.location.origin}/portal/${detailsModal.id}`;
                    return (
                      <div style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
                        <div style={{ fontSize: 12, color: "#6D28D9", fontWeight: 700, marginBottom: 6 }}>Student Result Portal Link</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ flex: 1, fontSize: 12, color: "#4C1D95", wordBreak: "break-all" }}>{portalUrl}</span>
                          <button
                            onClick={() => copyPortalLink(portalUrl)}
                            style={{ flexShrink: 0, padding: "5px 12px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: portalCopied ? "#16A34A" : "#7C3AED", color: "#fff", transition: "background .2s" }}>
                            {portalCopied ? "Copied!" : "Copy"}
                          </button>
                          <a href={portalUrl} target="_blank" rel="noreferrer"
                            style={{ flexShrink: 0, padding: "5px 10px", borderRadius: 7, border: "1px solid #7C3AED", fontSize: 12, fontWeight: 700, color: "#7C3AED", textDecoration: "none", background: "#fff" }}>
                            Open
                          </a>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Assign Template */}
                  <div className="form-section-title">Assign Result Template</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <select value={assignTplId} onChange={(e) => setAssignTplId(e.target.value)} style={{ flex: 1 }}>
                      <option value="">-- Select Template --</option>
                      {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({VIEW_META[t.view_type]?.label || t.view_type})</option>)}
                    </select>
                    <button className="btn" onClick={saveTemplate} disabled={!assignTplId}>Save</button>
                  </div>
                  {assignMsg && <div className={assignMsg.includes("success") ? "success-text" : "error-text"} style={{ marginBottom: 12, fontSize: 13 }}>{assignMsg}</div>}

                  {/* Extend Subscription */}
                  <div className="form-section-title">Extend Subscription</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                    <select value={subPlanId} onChange={(e) => setSubPlanId(e.target.value)} style={{ flex: 1, minWidth: 160 }}>
                      <option value="">-- Keep Current Plan --</option>
                      {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <div className="form-field" style={{ margin: 0, flex: "0 0 100px" }}>
                      <input type="number" min="1" value={subDays} onChange={(e) => setSubDays(e.target.value)} placeholder="Days" />
                    </div>
                    <span className="text-muted" style={{ fontSize: 13 }}>days</span>
                    <button className="btn" onClick={extendSub}>Extend</button>
                  </div>
                  {subMsg && <div className="success-text" style={{ marginBottom: 12, fontSize: 13 }}>{subMsg}</div>}

                  {/* School Admins */}
                  <div className="form-section-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>School Admin Accounts</span>
                    <button className="btn sm" onClick={() => { setShowAddAdmin(true); setAddAdminMsg(""); setAddAdminErr(""); }}>+ Add Admin</button>
                  </div>

                  {showAddAdmin && (
                    <form onSubmit={submitAddAdmin} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 14, marginBottom: 12 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                          Name *<input value={addAdminForm.name} onChange={(e) => setAddAdminForm({ ...addAdminForm, name: e.target.value })} required />
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                          Username *<input value={addAdminForm.username} onChange={(e) => setAddAdminForm({ ...addAdminForm, username: e.target.value })} required />
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                          Password * (min 8)<input type="password" value={addAdminForm.password} onChange={(e) => setAddAdminForm({ ...addAdminForm, password: e.target.value })} required />
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                          Email<input type="email" value={addAdminForm.email} onChange={(e) => setAddAdminForm({ ...addAdminForm, email: e.target.value })} />
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                          Mobile<input value={addAdminForm.mobile} onChange={(e) => setAddAdminForm({ ...addAdminForm, mobile: e.target.value })} />
                        </label>
                      </div>
                      {addAdminErr && <div className="error-text" style={{ fontSize: 12, marginBottom: 6 }}>{addAdminErr}</div>}
                      {addAdminMsg && <div className="success-text" style={{ fontSize: 12, marginBottom: 6 }}>{addAdminMsg}</div>}
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn" type="submit" disabled={addAdminSaving}>{addAdminSaving ? "Creating..." : "Create Admin"}</button>
                        <button className="btn secondary" type="button" onClick={() => setShowAddAdmin(false)}>Cancel</button>
                      </div>
                    </form>
                  )}

                  {adminsLoading ? (
                    <p className="text-muted" style={{ fontSize: 13 }}>Loading admins...</p>
                  ) : schoolAdmins.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: 13 }}>No admin accounts found.</p>
                  ) : (
                    <table style={{ fontSize: 13, marginBottom: 16 }}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Mobile</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schoolAdmins.map((a) => (
                          <tr key={a.id}>
                            <td>{a.name}</td>
                            <td><code style={{ fontSize: 12 }}>{a.username}</code></td>
                            <td>{a.email || "—"}</td>
                            <td>{a.mobile || "—"}</td>
                            <td><span className={`badge ${a.is_active ? "active" : "suspended"}`}>{a.is_active ? "Active" : "Inactive"}</span></td>
                            <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              <button className="btn sm secondary" onClick={() => openEditAdmin(a)}>Edit</button>
                              <button className="btn sm secondary" onClick={() => openEditCreds(a)}>Credentials</button>
                              <button className={`btn sm ${a.is_active ? "danger" : "success"}`} onClick={() => toggleAdminStatus(a)}>
                                {a.is_active ? "Deactivate" : "Activate"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit School Modal */}
      {editSchool && (
        <div className="tpl-modal-overlay" onClick={() => setEditSchool(null)}>
          <div className="tpl-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Edit School Details</h3>
              <button className="tpl-modal-close" onClick={() => setEditSchool(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { label: "School Name *", key: "name" },
                { label: "Address", key: "address" },
                { label: "Contact Email", key: "contact_email", type: "email" },
                { label: "Contact Mobile", key: "contact_mobile" },
              ].map(({ label, key, type }) => (
                <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                  <span>{label}</span>
                  <input
                    type={type || "text"}
                    value={editSchoolForm[key] || ""}
                    onChange={(e) => setEditSchoolForm({ ...editSchoolForm, [key]: e.target.value })}
                  />
                </label>
              ))}
              {editSchoolMsg && (
                <p style={{ margin: 0, fontSize: 13, color: editSchoolMsg.includes("updated") ? "green" : "red" }}>
                  {editSchoolMsg}
                </p>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setEditSchool(null)}>Cancel</button>
                <button className="btn" onClick={saveEditSchool} disabled={editSchoolSaving || !editSchoolForm.name}>
                  {editSchoolSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Password Modal */}
      {sendPwdModal && (
        <div className="tpl-modal-overlay" onClick={() => setSendPwdModal(null)}>
          <div className="tpl-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Send Admin Password — {sendPwdModal.name}</h3>
              <button className="tpl-modal-close" onClick={() => setSendPwdModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>
                A new password will be set for the school admin and sent to their registered email and mobile number.
                Leave blank to auto-generate a password.
              </p>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                <span>New Password (optional — leave blank to auto-generate)</span>
                <input
                  type="text"
                  placeholder="Leave blank to auto-generate"
                  value={sendPwdValue}
                  onChange={(e) => setSendPwdValue(e.target.value)}
                />
              </label>
              {sendPwdMsg && (
                <div style={{
                  fontSize: 13,
                  padding: "10px 12px",
                  background: sendPwdMsg.includes("failed") || sendPwdMsg.includes("Failed") ? "#fff3f3" : "#f0fff4",
                  border: `1px solid ${sendPwdMsg.includes("failed") || sendPwdMsg.includes("Failed") ? "#fca5a5" : "#86efac"}`,
                  borderRadius: 6,
                  wordBreak: "break-all",
                }}>
                  {sendPwdMsg}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setSendPwdModal(null)}>Close</button>
                <button className="btn" onClick={doSendPassword} disabled={sendPwdLoading}>
                  {sendPwdLoading ? "Sending..." : "Set & Send Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admin Details Modal */}
      {editAdmin && (
        <div className="tpl-modal-overlay" onClick={() => setEditAdmin(null)}>
          <div className="tpl-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Edit Admin — {editAdmin.username}</h3>
              <button className="tpl-modal-close" onClick={() => setEditAdmin(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { label: "Name *", key: "name" },
                { label: "Username *", key: "username" },
                { label: "Email", key: "email", type: "email" },
                { label: "Mobile", key: "mobile" },
              ].map(({ label, key, type }) => (
                <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                  <span>{label}</span>
                  <input type={type || "text"} value={editAdminForm[key] || ""} onChange={(e) => setEditAdminForm({ ...editAdminForm, [key]: e.target.value })} />
                </label>
              ))}
              {editAdminMsg && <p style={{ margin: 0, fontSize: 13, color: editAdminMsg.includes("Saved") ? "green" : "red" }}>{editAdminMsg}</p>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setEditAdmin(null)}>Cancel</button>
                <button className="btn" onClick={saveEditAdmin} disabled={editAdminSaving || !editAdminForm.name}>
                  {editAdminSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admin Credentials Modal */}
      {editAdminCreds && (
        <div className="tpl-modal-overlay" onClick={() => setEditAdminCreds(null)}>
          <div className="tpl-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Change Credentials — {editAdminCreds.username}</h3>
              <button className="tpl-modal-close" onClick={() => setEditAdminCreds(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                <span>New Username</span>
                <input value={editCredsForm.username} onChange={(e) => setEditCredsForm({ ...editCredsForm, username: e.target.value })} placeholder="Leave unchanged to keep current" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                <span>New Password (min 8)</span>
                <input type="password" value={editCredsForm.password} onChange={(e) => setEditCredsForm({ ...editCredsForm, password: e.target.value })} placeholder="Leave blank to keep current" />
              </label>
              {editCredsMsg && <p style={{ margin: 0, fontSize: 13, color: editCredsMsg.includes("updated") ? "green" : "red" }}>{editCredsMsg}</p>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setEditAdminCreds(null)}>Cancel</button>
                <button className="btn" onClick={saveEditCreds} disabled={editCredsSaving}>
                  {editCredsSaving ? "Saving..." : "Update Credentials"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Teachers Tab
   ====================================================== */
function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [toggling, setToggling] = useState(null);
  const [editTeacher, setEditTeacher] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editMsg, setEditMsg] = useState("");

  // Add Teacher
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [addForm, setAddForm] = useState({ school_id: "", name: "", username: "", password: "", email: "", mobile: "", designation: "", qualification: "" });
  const [addMsg, setAddMsg] = useState("");
  const [addError, setAddError] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  function load() {
    setLoading(true);
    const url = schoolFilter ? `/super-admin/teachers?schoolId=${schoolFilter}` : "/super-admin/teachers";
    api.get(url)
      .then((r) => setTeachers(r.data))
      .finally(() => setLoading(false));
  }
  useEffect(load, [schoolFilter]);

  useEffect(() => {
    api.get("/super-admin/schools").then((r) => setSchools(r.data));
  }, []);

  async function toggleStatus(teacher) {
    setToggling(teacher.id);
    try {
      await api.put(`/super-admin/teachers/${teacher.id}/toggle-status`);
      load();
    } catch {
      // ignore
    } finally {
      setToggling(null);
    }
  }

  function openEdit(t) {
    setEditTeacher(t);
    setEditForm({
      name: t.name || "",
      email: t.email || "",
      mobile: t.mobile || "",
      designation: t.designation || "",
      qualification: t.qualification || "",
      address: t.address || "",
    });
    setEditMsg("");
  }

  async function saveEdit() {
    setSaving(true);
    setEditMsg("");
    try {
      await api.put(`/super-admin/teachers/${editTeacher.id}`, editForm);
      setEditMsg("Saved successfully.");
      load();
      setTimeout(() => setEditTeacher(null), 800);
    } catch (err) {
      setEditMsg(err.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  async function submitAddTeacher(e) {
    e.preventDefault();
    setAddSaving(true); setAddMsg(""); setAddError("");
    try {
      const r = await api.post("/super-admin/teachers", { ...addForm, school_id: parseInt(addForm.school_id, 10) });
      setAddMsg(`Teacher "${r.data.name}" added to ${r.data.school}.`);
      setAddForm({ school_id: "", name: "", username: "", password: "", email: "", mobile: "", designation: "", qualification: "" });
      load();
    } catch (err) {
      setAddError(err.response?.data?.error || "Failed to add teacher.");
    } finally {
      setAddSaving(false);
    }
  }

  const filtered = teachers.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.name?.toLowerCase().includes(q) ||
      t.username?.toLowerCase().includes(q) ||
      t.School?.name?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">All Teachers — Platform Wide</h1>
        <p className="subtitle">Manage teacher accounts across all schools.</p>
      </div>

      <div className="card">
        <div className="card-header" style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">All Schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <input
            placeholder="Search by name, username or school..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
          />
          <span className="badge info">{filtered.length} teachers</span>
          <button className="btn sm" onClick={() => { setShowAddTeacher(true); setAddMsg(""); setAddError(""); }}>
            + Add Teacher
          </button>
        </div>

        {loading ? (
          <p className="text-muted">Loading teachers...</p>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👩‍🏫</div>
            <div className="empty-state-text">{search ? "No teachers match your search." : "No teachers found."}</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Teacher Name</th>
                <th>Username</th>
                <th>School</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id}>
                  <td><strong>{t.name}</strong></td>
                  <td style={{ fontFamily: "monospace", fontSize: 13 }}>{t.username}</td>
                  <td>{t.School?.name || <span className="text-muted">—</span>}</td>
                  <td style={{ fontSize: 13, color: "var(--muted)" }}>{t.email || "—"}</td>
                  <td style={{ fontSize: 13, color: "var(--muted)" }}>{t.mobile || "—"}</td>
                  <td>
                    <span className={`badge ${t.is_active !== false ? "active" : "suspended"}`}>
                      {t.is_active !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <button className="btn sm secondary" onClick={() => openEdit(t)}>Edit</button>
                    <button
                      className={`btn sm ${t.is_active !== false ? "danger" : "success"}`}
                      onClick={() => toggleStatus(t)}
                      disabled={toggling === t.id}
                    >
                      {toggling === t.id ? "..." : t.is_active !== false ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Teacher Modal */}
      {editTeacher && (
        <div className="tpl-modal-overlay" onClick={() => setEditTeacher(null)}>
          <div className="tpl-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Edit Teacher</h3>
              <button className="tpl-modal-close" onClick={() => setEditTeacher(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>
                School: <strong>{editTeacher.School?.name}</strong> &nbsp;|&nbsp; Username: <code>{editTeacher.username}</code>
              </p>
              {[
                { label: "Full Name", key: "name" },
                { label: "Email", key: "email", type: "email" },
                { label: "Mobile", key: "mobile" },
                { label: "Designation", key: "designation" },
                { label: "Qualification", key: "qualification" },
                { label: "Address", key: "address" },
              ].map(({ label, key, type }) => (
                <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                  <span>{label}</span>
                  <input
                    type={type || "text"}
                    value={editForm[key]}
                    onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                  />
                </label>
              ))}
              {editMsg && (
                <p style={{ margin: 0, fontSize: 13, color: editMsg.includes("success") ? "green" : "red" }}>{editMsg}</p>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setEditTeacher(null)}>Cancel</button>
                <button className="btn primary" onClick={saveEdit} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Teacher Modal */}
      {showAddTeacher && (
        <div className="tpl-modal-overlay" onClick={() => setShowAddTeacher(false)}>
          <div className="tpl-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Add Teacher</h3>
              <button className="tpl-modal-close" onClick={() => setShowAddTeacher(false)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              <form onSubmit={submitAddTeacher} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                  <span>School *</span>
                  <select
                    value={addForm.school_id}
                    onChange={(e) => setAddForm({ ...addForm, school_id: e.target.value })}
                    required
                  >
                    <option value="">-- Select School --</option>
                    {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    { label: "Full Name *", key: "name", required: true },
                    { label: "Username *", key: "username", required: true },
                    { label: "Password *", key: "password", type: "password", required: true },
                    { label: "Email", key: "email", type: "email" },
                    { label: "Mobile", key: "mobile" },
                    { label: "Designation", key: "designation" },
                    { label: "Qualification", key: "qualification" },
                  ].map(({ label, key, type, required }) => (
                    <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                      <span>{label}</span>
                      <input
                        type={type || "text"}
                        value={addForm[key]}
                        onChange={(e) => setAddForm({ ...addForm, [key]: e.target.value })}
                        required={required}
                      />
                    </label>
                  ))}
                </div>
                {addMsg && <div className="success-text" style={{ fontSize: 13 }}>{addMsg}</div>}
                {addError && <div className="error-text" style={{ fontSize: 13 }}>{addError}</div>}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button type="button" className="btn secondary" onClick={() => setShowAddTeacher(false)}>Cancel</button>
                  <button type="submit" className="btn" disabled={addSaving}>
                    {addSaving ? "Adding..." : "Add Teacher"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Students Tab
   ====================================================== */
function Students() {
  const [students, setStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classSections, setClassSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [editStudent, setEditStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editMsg, setEditMsg] = useState("");

  function load() {
    setLoading(true);
    const params = new URLSearchParams();
    if (schoolFilter) params.set("schoolId", schoolFilter);
    if (sectionFilter) params.set("classSectionId", sectionFilter);
    api.get(`/super-admin/students?${params.toString()}`)
      .then((r) => setStudents(r.data))
      .finally(() => setLoading(false));
  }
  useEffect(load, [schoolFilter, sectionFilter]);

  useEffect(() => {
    api.get("/super-admin/schools").then((r) => setSchools(r.data));
  }, []);

  useEffect(() => {
    if (!schoolFilter) { setClassSections([]); setSectionFilter(""); return; }
    api.get(`/super-admin/schools/${schoolFilter}/class-sections`).then((r) => {
      setClassSections(r.data);
      setSectionFilter("");
    });
  }, [schoolFilter]);

  function openEdit(s) {
    setEditStudent(s);
    setEditForm({
      name: s.name || "",
      roll_no: s.roll_no || "",
      mobile: s.mobile || "",
      dob: s.dob ? s.dob.slice(0, 10) : "",
      parent_name: s.parent_name || "",
      address: s.address || "",
      is_active: s.is_active !== false,
    });
    setEditMsg("");
  }

  async function saveEdit() {
    setSaving(true);
    setEditMsg("");
    try {
      await api.put(`/super-admin/students/${editStudent.id}`, editForm);
      setEditMsg("Saved successfully.");
      load();
      setTimeout(() => setEditStudent(null), 800);
    } catch (err) {
      setEditMsg(err.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  const filtered = students.filter((s) => {
    const q = search.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.roll_no?.toString().includes(q) ||
      s.School?.name?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">All Students — Platform Wide</h1>
        <p className="subtitle">View and manage student records across all schools.</p>
      </div>

      <div className="card">
        <div className="card-header" style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">All Schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {schoolFilter && (
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <option value="">All Classes</option>
              {classSections.map((cs) => (
                <option key={cs.id} value={cs.id}>
                  {cs.name}{cs.section ? `-${cs.section}` : ""}
                </option>
              ))}
            </select>
          )}
          <input
            placeholder="Search by name, roll no or school..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
          />
          <span className="badge info">{filtered.length} students</span>
        </div>

        {loading ? (
          <p className="text-muted">Loading students...</p>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🎒</div>
            <div className="empty-state-text">{search ? "No students match your search." : "No students found."}</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Roll No</th>
                <th>Class</th>
                <th>School</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td>
                  <td style={{ fontSize: 13 }}>{s.roll_no || "—"}</td>
                  <td style={{ fontSize: 13 }}>
                    {s.ClassSection ? `${s.ClassSection.name}${s.ClassSection.section ? `-${s.ClassSection.section}` : ""}` : "—"}
                  </td>
                  <td>{s.School?.name || <span className="text-muted">—</span>}</td>
                  <td>
                    <span className={`badge ${s.is_active !== false ? "active" : "suspended"}`}>
                      {s.is_active !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button className="btn sm secondary" onClick={() => openEdit(s)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Student Modal */}
      {editStudent && (
        <div className="tpl-modal-overlay" onClick={() => setEditStudent(null)}>
          <div className="tpl-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Edit Student</h3>
              <button className="tpl-modal-close" onClick={() => setEditStudent(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>
                School: <strong>{editStudent.School?.name}</strong> &nbsp;|&nbsp;
                Class: <strong>{editStudent.ClassSection ? `${editStudent.ClassSection.name}${editStudent.ClassSection.section ? `-${editStudent.ClassSection.section}` : ""}` : "—"}</strong>
              </p>
              {[
                { label: "Full Name", key: "name" },
                { label: "Roll No", key: "roll_no" },
                { label: "Mobile", key: "mobile" },
                { label: "Date of Birth", key: "dob", type: "date" },
                { label: "Parent / Guardian Name", key: "parent_name" },
                { label: "Address", key: "address" },
              ].map(({ label, key, type }) => (
                <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                  <span>{label}</span>
                  <input
                    type={type || "text"}
                    value={editForm[key]}
                    onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                  />
                </label>
              ))}
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                Active
              </label>
              {editMsg && (
                <p style={{ margin: 0, fontSize: 13, color: editMsg.includes("success") ? "green" : "red" }}>{editMsg}</p>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setEditStudent(null)}>Cancel</button>
                <button className="btn primary" onClick={saveEdit} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Plans Tab
   ====================================================== */
function Plans() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", duration_days: 365 });
  const [formError, setFormError] = useState("");
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editMsg, setEditMsg] = useState("");

  function load() { api.get("/super-admin/plans").then((r) => setPlans(r.data)); }
  useEffect(load, []);

  async function createPlan(e) {
    e.preventDefault();
    setFormError("");
    try {
      await api.post("/super-admin/plans", {
        ...form,
        price: Number(form.price),
        duration_days: Number(form.duration_days),
        feature_flags: { maxExamViews: 4, graphs: true, bulkPrint: true, excelExport: true },
      });
      setForm({ name: "", price: "", duration_days: 365 });
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to create plan.");
    }
  }

  function startEdit(plan) {
    setEditId(plan.id);
    setEditForm({ name: plan.name, price: plan.price, duration_days: plan.duration_days });
    setEditMsg("");
  }

  async function saveEdit() {
    setEditMsg("");
    try {
      await api.put(`/super-admin/plans/${editId}`, {
        ...editForm,
        price: Number(editForm.price),
        duration_days: Number(editForm.duration_days),
      });
      setEditMsg("Plan updated.");
      setEditId(null);
      load();
    } catch (err) {
      setEditMsg(err.response?.data?.error || "Failed to update plan.");
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Subscription Plans</h1>
        <p className="subtitle">Define the plans schools can subscribe to.</p>
      </div>

      <div className="card">
        <h3>Add a New Plan</h3>
        {formError && <div className="error-text" style={{ marginBottom: 12 }}>{formError}</div>}
        <form onSubmit={createPlan}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Plan Name *</label>
              <input placeholder="e.g. Basic, Standard, Premium" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-field">
              <label className="form-label">Price (INR) *</label>
              <input type="number" placeholder="e.g. 4999" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required min="0" />
            </div>
            <div className="form-field">
              <label className="form-label">Duration (days) *</label>
              <input type="number" placeholder="365" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} required min="1" />
            </div>
          </div>
          <button className="btn" type="submit">Add Plan</button>
        </form>
      </div>

      <div className="card">
        <h3>Existing Plans</h3>
        {editMsg && <div className="success-text" style={{ marginBottom: 10, fontSize: 13 }}>{editMsg}</div>}
        {plans.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💳</div>
            <div className="empty-state-text">No plans yet. Create one above.</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Plan Name</th>
                <th>Price</th>
                <th>Duration</th>
                <th>Max Exam Views</th>
                <th>Features</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <React.Fragment key={p.id}>
                  <tr>
                    <td><strong>{p.name}</strong></td>
                    <td>₹{Number(p.price).toLocaleString("en-IN")}</td>
                    <td>{p.duration_days} days</td>
                    <td>{p.feature_flags?.maxExamViews || "—"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {p.feature_flags?.graphs && <span className="badge info">Graphs</span>}
                        {p.feature_flags?.bulkPrint && <span className="badge info">Bulk Print</span>}
                        {p.feature_flags?.excelExport && <span className="badge info">Excel</span>}
                      </div>
                    </td>
                    <td>
                      <button className="btn sm secondary" onClick={() => startEdit(p)}>Edit</button>
                    </td>
                  </tr>
                  {editId === p.id && (
                    <tr>
                      <td colSpan={6} style={{ background: "var(--surface-2, #F8FAFC)", padding: 16 }}>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                          <div className="form-field" style={{ margin: 0, flex: 1, minWidth: 160 }}>
                            <label className="form-label">Plan Name</label>
                            <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                          </div>
                          <div className="form-field" style={{ margin: 0, flex: "0 0 120px" }}>
                            <label className="form-label">Price (INR)</label>
                            <input type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
                          </div>
                          <div className="form-field" style={{ margin: 0, flex: "0 0 110px" }}>
                            <label className="form-label">Duration (days)</label>
                            <input type="number" value={editForm.duration_days} onChange={(e) => setEditForm({ ...editForm, duration_days: e.target.value })} />
                          </div>
                          <button className="btn" onClick={saveEdit}>Save</button>
                          <button className="btn secondary" onClick={() => setEditId(null)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ======================================================
   Templates Tab — Full Rebuild with visual gallery
   ====================================================== */
const DEFAULT_TPL_FORM = {
  name: "",
  view_type: "one_exam",
  themeColor: "#1E3A5F",
  accentColor: "#F5C842",
  examLabels: ["", "", "", ""],
  showGraph: false,
  hasCoScholastic: false,
  coScholasticScale: "grade_only",
  coScholasticActivities: ["Work Education", "Art Education", "Health & Physical Education", "Discipline"],
  showTeacherRemark: false,
  showPhoto: false,
  showRank: true,
  showPercentage: true,
  subtitle: "",
  planRestrictions: [],
};

function buildLayoutConfig(f) {
  const cols = VIEW_META[f.view_type]?.cols || 1;
  return {
    themeColor: f.themeColor,
    accentColor: f.accentColor,
    examLabels: f.examLabels.slice(0, cols).filter(Boolean),
    showGraph: f.showGraph,
    hasCoScholastic: f.hasCoScholastic,
    coScholasticScale: f.coScholasticScale,
    coScholasticActivities: f.hasCoScholastic ? f.coScholasticActivities.filter(Boolean) : [],
    showTeacherRemark: f.showTeacherRemark,
    showPhoto: f.showPhoto,
    showRank: f.showRank,
    showPercentage: f.showPercentage,
    subtitle: f.subtitle,
    planRestrictions: f.planRestrictions,
  };
}

function formFromTemplate(tpl) {
  const cfg = parseCfg(tpl.layout_config);
  const cols = VIEW_META[tpl.view_type]?.cols || 1;
  const labels = cfg.examLabels || [];
  // pad to 4
  const padded = [...labels];
  while (padded.length < 4) padded.push("");
  return {
    name: tpl.name,
    view_type: tpl.view_type,
    themeColor: cfg.themeColor || "#1E3A5F",
    accentColor: cfg.accentColor || "#F5C842",
    examLabels: padded,
    showGraph: !!cfg.showGraph,
    hasCoScholastic: !!cfg.hasCoScholastic,
    coScholasticScale: cfg.coScholasticScale || "grade_only",
    coScholasticActivities: cfg.coScholasticActivities?.length ? cfg.coScholasticActivities : ["Work Education", "Art Education", "Health & Physical Education", "Discipline"],
    showTeacherRemark: !!cfg.showTeacherRemark,
    showPhoto: !!cfg.showPhoto,
    showRank: cfg.showRank !== false,
    showPercentage: cfg.showPercentage !== false,
    subtitle: cfg.subtitle || "",
    planRestrictions: cfg.planRestrictions || [],
  };
}

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create new
  const [form, setForm] = useState(DEFAULT_TPL_FORM);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [preview, setPreview] = useState(null); // template for full preview modal
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  function load() { api.get("/super-admin/templates").then((r) => setTemplates(r.data)); }
  useEffect(load, []);
  useEffect(() => { api.get("/super-admin/plans").then((r) => setPlans(r.data)); }, []);

  function openCreate() {
    setEditingId(null);
    setForm(DEFAULT_TPL_FORM);
    setSaveMsg(""); setSaveErr("");
    setPanelOpen(true);
  }

  function openEdit(tpl) {
    setEditingId(tpl.id);
    setForm(formFromTemplate(tpl));
    setSaveMsg(""); setSaveErr("");
    setPanelOpen(true);
  }

  async function saveTemplate() {
    setSaving(true); setSaveMsg(""); setSaveErr("");
    try {
      const payload = {
        name: form.name,
        view_type: form.view_type,
        layout_config: JSON.stringify(buildLayoutConfig(form)),
        is_active: true,
      };
      if (editingId) {
        await api.put(`/super-admin/templates/${editingId}`, payload);
        setSaveMsg("Template updated successfully.");
      } else {
        await api.post("/super-admin/templates", payload);
        setSaveMsg("Template created successfully.");
      }
      load();
    } catch (err) {
      setSaveErr(err.response?.data?.error || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteTemplate(id) {
    try {
      await api.delete(`/super-admin/templates/${id}`);
      setDeleteConfirm(null);
      load();
    } catch (err) {
      // ignore
    }
  }

  function setF(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  const cols = VIEW_META[form.view_type]?.cols || 1;

  // Build a live preview template object from form state
  const livePreviewTpl = {
    name: form.name || "Preview Template",
    view_type: form.view_type,
    layout_config: JSON.stringify(buildLayoutConfig(form)),
  };

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="page-title">Result Template Library</h1>
          <p className="subtitle">Define result card layouts that schools can choose from.</p>
        </div>
        <button className="btn" onClick={openCreate}>+ Create New Template</button>
      </div>

      {/* Template gallery */}
      {templates.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-text">No templates yet. Create the first one.</div>
          </div>
        </div>
      ) : (
        <div className="tpl-gallery">
          {templates.map((tpl) => {
            const meta = VIEW_META[tpl.view_type] || { cols: 1, label: tpl.view_type };
            return (
              <div key={tpl.id} className="tpl-gallery-item">
                <div className="tpl-gallery-preview" onClick={() => setPreview(tpl)} style={{ cursor: "zoom-in" }}>
                  <MiniResultCard tpl={tpl} cols={meta.cols} />
                </div>
                <div className="tpl-gallery-footer">
                  <div>
                    <div className="tpl-gallery-name">{tpl.name}</div>
                    <div className="tpl-gallery-meta">{meta.label}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn sm secondary" onClick={() => openEdit(tpl)}>Edit</button>
                    <button className="btn sm danger" onClick={() => setDeleteConfirm(tpl)}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Side Panel for Create/Edit */}
      {panelOpen && (
        <div className="tpl-modal-overlay" onClick={() => setPanelOpen(false)}>
          <div
            className="tpl-modal"
            style={{ maxWidth: 900, width: "98%", display: "flex", flexDirection: "row", gap: 0, padding: 0, overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Form */}
            <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px", minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h3 style={{ margin: 0 }}>{editingId ? "Edit Template" : "Create New Template"}</h3>
                <button className="tpl-modal-close" onClick={() => setPanelOpen(false)}>✕</button>
              </div>

              {saveMsg && <div className="success-text" style={{ marginBottom: 12 }}>{saveMsg}</div>}
              {saveErr && <div className="error-text" style={{ marginBottom: 12 }}>{saveErr}</div>}

              <div className="form-field">
                <label className="form-label">Template Name *</label>
                <input placeholder="e.g. Standard Result Card" value={form.name} onChange={(e) => setF("name", e.target.value)} />
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">View Type</label>
                  <select value={form.view_type} onChange={(e) => setF("view_type", e.target.value)}>
                    <option value="one_exam">One-Exam View</option>
                    <option value="two_exam">Two-Exam View</option>
                    <option value="three_exam">Three-Exam View</option>
                    <option value="four_exam">Four-Exam View</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Usage Hint / Subtitle</label>
                  <input placeholder="e.g. For CBSE schools, Class 1–8" value={form.subtitle} onChange={(e) => setF("subtitle", e.target.value)} />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Theme Color</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="color" value={form.themeColor} onChange={(e) => setF("themeColor", e.target.value)} style={{ width: 48, height: 36, padding: 2, borderRadius: 6, border: "1px solid var(--border)", cursor: "pointer" }} />
                    <input value={form.themeColor} onChange={(e) => setF("themeColor", e.target.value)} style={{ flex: 1, fontFamily: "monospace" }} placeholder="#1E3A5F" />
                  </div>
                </div>
                <div className="form-field">
                  <label className="form-label">Accent Color</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="color" value={form.accentColor} onChange={(e) => setF("accentColor", e.target.value)} style={{ width: 48, height: 36, padding: 2, borderRadius: 6, border: "1px solid var(--border)", cursor: "pointer" }} />
                    <input value={form.accentColor} onChange={(e) => setF("accentColor", e.target.value)} style={{ flex: 1, fontFamily: "monospace" }} placeholder="#F5C842" />
                  </div>
                </div>
              </div>

              {/* Custom Exam Labels */}
              <div className="form-field">
                <label className="form-label">Custom Exam Labels ({cols} exam{cols > 1 ? "s" : ""})</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {Array.from({ length: cols }).map((_, i) => (
                    <input
                      key={i}
                      placeholder={`Exam ${i + 1} label`}
                      value={form.examLabels[i] || ""}
                      onChange={(e) => {
                        const labels = [...form.examLabels];
                        labels[i] = e.target.value;
                        setF("examLabels", labels);
                      }}
                      style={{ flex: 1, minWidth: 120 }}
                    />
                  ))}
                </div>
              </div>

              {/* Checkboxes row */}
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 16 }}>
                {[
                  { key: "showGraph", label: "Show Performance Graph" },
                  { key: "hasCoScholastic", label: "Has Co-Scholastic Activities" },
                  { key: "showTeacherRemark", label: "Show Teacher Remarks" },
                  { key: "showPhoto", label: "Show Student Photo" },
                  { key: "showRank", label: "Show Class Rank" },
                  { key: "showPercentage", label: "Show Percentage" },
                ].map(({ key, label }) => (
                  <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!form[key]} onChange={(e) => setF(key, e.target.checked)} />
                    {label}
                  </label>
                ))}
              </div>

              {/* Co-Scholastic options */}
              {form.hasCoScholastic && (
                <div style={{ background: "var(--surface-2, #F8FAFC)", borderRadius: 8, padding: 16, marginBottom: 16, border: "1px solid var(--border)" }}>
                  <div className="form-field" style={{ marginBottom: 12 }}>
                    <label className="form-label">Co-Scholastic Scale</label>
                    <select value={form.coScholasticScale} onChange={(e) => setF("coScholasticScale", e.target.value)}>
                      <option value="grade_only">Grade Only</option>
                      <option value="marks_and_grade">Marks + Grade</option>
                    </select>
                  </div>
                  <label className="form-label">Activities</label>
                  {form.coScholasticActivities.map((act, i) => (
                    <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <input
                        value={act}
                        onChange={(e) => {
                          const arr = [...form.coScholasticActivities];
                          arr[i] = e.target.value;
                          setF("coScholasticActivities", arr);
                        }}
                        placeholder={`Activity ${i + 1}`}
                        style={{ flex: 1 }}
                      />
                      <button type="button" className="btn sm danger" onClick={() => setF("coScholasticActivities", form.coScholasticActivities.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="btn sm secondary" onClick={() => setF("coScholasticActivities", [...form.coScholasticActivities, ""])}>+ Add Activity</button>
                </div>
              )}

              {/* Plan Restrictions */}
              {plans.length > 0 && (
                <div className="form-field">
                  <label className="form-label">Plan Restrictions (leave blank for all plans)</label>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {plans.map((p) => (
                      <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={form.planRestrictions.includes(p.id)}
                          onChange={(e) => {
                            const arr = e.target.checked
                              ? [...form.planRestrictions, p.id]
                              : form.planRestrictions.filter((id) => id !== p.id);
                            setF("planRestrictions", arr);
                          }}
                        />
                        {p.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button className="btn" onClick={saveTemplate} disabled={saving || !form.name}>
                {saving ? "Saving..." : editingId ? "Update Template" : "Create Template"}
              </button>
            </div>

            {/* Live Preview */}
            <div style={{ width: 320, borderLeft: "1px solid var(--border)", background: "var(--surface-2, #F8FAFC)", padding: "20px 16px", overflowY: "auto", flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Live Preview</div>
              <MiniResultCard tpl={livePreviewTpl} cols={cols} />
            </div>
          </div>
        </div>
      )}

      {/* Full preview modal */}
      {preview && (
        <div className="tpl-modal-overlay" onClick={() => setPreview(null)}>
          <div className="tpl-modal tpl-modal-a4" style={{ maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div>
                <strong>{preview.name}</strong>
                <span className="text-muted" style={{ marginLeft: 10, fontSize: 13 }}>A4 Preview</span>
              </div>
              <button className="tpl-modal-close" onClick={() => setPreview(null)}>✕</button>
            </div>
            <div className="tpl-modal-body tpl-modal-body-a4">
              <FullResultCard tpl={preview} cols={VIEW_META[preview.view_type]?.cols || 1} />
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="tpl-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="tpl-modal" style={{ maxWidth: 420, width: "95%", padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px" }}>Delete Template?</h3>
            <p style={{ color: "var(--muted)", marginBottom: 20 }}>
              Are you sure you want to deactivate <strong>{deleteConfirm.name}</strong>? This will hide it from schools but can be restored if needed.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn danger" onClick={() => deleteTemplate(deleteConfirm.id)}>Yes, Delete</button>
              <button className="btn secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Gateway Settings Tab
   ====================================================== */
const GW_TABS = [
  { key: "email",           label: "📧 Email (SMTP)" },
  { key: "sms",             label: "📱 SMS (PrimeClick IT)" },
  { key: "whatsapp",        label: "💬 WhatsApp" },
  { key: "razorpay",        label: "💳 Razorpay" },
  { key: "notif_templates", label: "✉️ Message Templates" },
];

function GatewaySettings() {
  const [gwTab, setGwTab] = useState("email");
  const [overview, setOverview] = useState(null);

  function reloadOverview() {
    api.get("/super-admin/settings/overview").then((r) => setOverview(r.data)).catch(() => {});
  }
  useEffect(reloadOverview, []);

  function StatusPill({ ok }) {
    return <span className={`badge ${ok ? "active" : "suspended"}`}>{ok ? "Configured" : "Not Set"}</span>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Gateway Settings</h1>
        <p className="subtitle">Configure Email, SMS, WhatsApp, and Payment gateways.</p>
      </div>

      {overview && (
        <div className="stat-grid" style={{ marginBottom: 24 }}>
          {[
            { label: "Email (SMTP)",    icon: "📧", ok: overview.email?.configured,    extra: overview.email?.enabled ? "Enabled" : "Disabled" },
            { label: "SMS",             icon: "📱", ok: overview.sms?.configured,      extra: overview.sms?.enabled ? "Enabled" : "Disabled" },
            { label: "WhatsApp",        icon: "💬", ok: overview.whatsapp?.configured, extra: overview.whatsapp?.enabled ? "Enabled" : "Disabled" },
            { label: "Razorpay",        icon: "💳", ok: overview.razorpay?.configured, extra: overview.razorpay?.mode === "live" ? "🔴 LIVE" : "🟡 Test" },
          ].map((g) => (
            <div className="stat-card" key={g.label} style={{ cursor: "default" }}>
              <div className="stat-card-icon">{g.icon}</div>
              <div style={{ marginTop: 6 }}><StatusPill ok={g.ok} /></div>
              <div className="stat-card-label" style={{ marginTop: 4 }}>{g.label}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{g.extra}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {GW_TABS.map((t) => (
          <button
            key={t.key}
            className={`btn ${gwTab === t.key ? "" : "secondary"}`}
            onClick={() => setGwTab(t.key)}
            style={{ fontSize: 13 }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {gwTab === "email"           && <EmailSettings    onSaved={reloadOverview} />}
      {gwTab === "sms"             && <SmsSettings      onSaved={reloadOverview} />}
      {gwTab === "whatsapp"        && <WhatsAppSettings onSaved={reloadOverview} />}
      {gwTab === "razorpay"        && <RazorpaySettings onSaved={reloadOverview} />}
      {gwTab === "notif_templates" && <NotifTemplates />}
    </div>
  );
}

/* ── Email Settings ─────────────────────────────────── */
function EmailSettings({ onSaved }) {
  const [form, setForm] = useState({
    smtp_host: "", smtp_port: "587", smtp_user: "", smtp_pass: "",
    smtp_encrypt: "tls", smtp_from: "", smtp_from_name: "", email_enabled: "true",
  });
  const [testTo, setTestTo] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/super-admin/settings/email").then((r) => {
      setForm((f) => ({ ...f, ...r.data.settings }));
    }).catch(() => {});
  }, []);

  async function save(e) {
    e.preventDefault(); setMsg(""); setErr("");
    try {
      await api.put("/super-admin/settings/email", form);
      setMsg("Email settings saved successfully.");
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.error || "Save failed."); }
  }

  async function sendTest() {
    setMsg(""); setErr(""); setLoading(true);
    try {
      const r = await api.post("/super-admin/settings/test/email", { to: testTo });
      setMsg(r.data.message);
    } catch (ex) { setErr(ex.response?.data?.error || "Test failed."); }
    finally { setLoading(false); }
  }

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div className="card">
      <div className="gw-card-header">
        <h3 style={{ margin: 0 }}>📧 SMTP Email Settings</h3>
        <label className="toggle-switch">
          <input type="checkbox" checked={form.email_enabled !== "false"} onChange={(e) => setForm({ ...form, email_enabled: e.target.checked ? "true" : "false" })} />
          <span className="toggle-slider" />
        </label>
      </div>
      {msg && <div className="success-text" style={{ marginBottom: 12 }}>{msg}</div>}
      {err && <div className="error-text"   style={{ marginBottom: 12 }}>{err}</div>}
      <form onSubmit={save}>
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">SMTP Host *</label>
            <input placeholder="smtp.hostinger.com" {...f("smtp_host")} required />
          </div>
          <div className="form-field">
            <label className="form-label">SMTP Port</label>
            <input type="number" placeholder="587" {...f("smtp_port")} />
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>
              587 = TLS/STARTTLS &nbsp;·&nbsp; 465 = SSL &nbsp;·&nbsp; 25 = None
            </div>
          </div>
          <div className="form-field">
            <label className="form-label">Encryption</label>
            <select {...f("smtp_encrypt")}
              onChange={(e) => {
                const enc = e.target.value;
                const curPort = form.smtp_port;
                const autoPort =
                  enc === "ssl"  && (!curPort || curPort === "587") ? "465" :
                  enc === "tls"  && (!curPort || curPort === "465") ? "587" :
                  enc === "none" && (!curPort || curPort === "465") ? "25"  : curPort;
                setForm({ ...form, smtp_encrypt: enc, smtp_port: autoPort });
              }}
            >
              <option value="tls">TLS / STARTTLS (port 587)</option>
              <option value="ssl">SSL (port 465)</option>
              <option value="none">None (port 25)</option>
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">SMTP Username *</label>
            <input placeholder="noreply@yourdomain.com" {...f("smtp_user")} required />
          </div>
          <div className="form-field">
            <label className="form-label">SMTP Password</label>
            <input type="password" placeholder="Leave blank to keep existing" {...f("smtp_pass")} />
          </div>
          <div className="form-field">
            <label className="form-label">From Email</label>
            <input placeholder="noreply@yourdomain.com" {...f("smtp_from")} />
          </div>
          <div className="form-field">
            <label className="form-label">From Name</label>
            <input placeholder="Volcanic Result Manager" {...f("smtp_from_name")} />
          </div>
        </div>
        <button className="btn" type="submit">Save Email Settings</button>
      </form>
      <hr style={{ margin: "20px 0", border: "1px solid var(--border)" }} />
      <h4 style={{ margin: "0 0 12px" }}>Send Test Email</h4>
      <div style={{ display: "flex", gap: 8 }}>
        <input placeholder="Recipient email (optional)" value={testTo} onChange={(e) => setTestTo(e.target.value)} style={{ flex: 1 }} />
        <button className="btn secondary" onClick={sendTest} disabled={loading}>{loading ? "Sending…" : "Send Test"}</button>
      </div>
    </div>
  );
}

/* ── SMS Settings ───────────────────────────────────── */
function SmsSettings({ onSaved }) {
  const [form, setForm] = useState({
    sms_api_url: "http://sms.primeclick.in/api/mt/SendSMS",
    sms_user: "", sms_pass: "", sms_api_key: "",
    sms_sender: "VOLRES", sms_route: "15", sms_dlt_entity_id: "", sms_enabled: "true",
  });
  const [testPhone, setTestPhone] = useState("");
  const [balance, setBalance] = useState(null);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/super-admin/settings/sms").then((r) => setForm((f) => ({ ...f, ...r.data.settings }))).catch(() => {});
  }, []);

  async function save(e) {
    e.preventDefault(); setMsg(""); setErr("");
    try {
      await api.put("/super-admin/settings/sms", form);
      setMsg("SMS settings saved successfully."); onSaved();
    } catch (ex) { setErr(ex.response?.data?.error || "Save failed."); }
  }

  async function sendTest() {
    setMsg(""); setErr(""); setLoading(true);
    try {
      const r = await api.post("/super-admin/settings/test/sms", { phone: testPhone });
      setMsg(r.data.message);
    } catch (ex) { setErr(ex.response?.data?.error || "Test failed."); }
    finally { setLoading(false); }
  }

  async function checkBalance() {
    const r = await api.get("/super-admin/settings/balance/sms").catch(() => ({ data: { balance: "Error" } }));
    setBalance(r.data.balance);
  }

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div className="card">
      <div className="gw-card-header">
        <h3 style={{ margin: 0 }}>📱 SMS Settings — PrimeClick IT</h3>
        <label className="toggle-switch">
          <input type="checkbox" checked={form.sms_enabled !== "false"} onChange={(e) => setForm({ ...form, sms_enabled: e.target.checked ? "true" : "false" })} />
          <span className="toggle-slider" />
        </label>
      </div>
      {msg && <div className="success-text" style={{ marginBottom: 12 }}>{msg}</div>}
      {err && <div className="error-text"   style={{ marginBottom: 12 }}>{err}</div>}
      <form onSubmit={save}>
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">API URL</label>
            <input {...f("sms_api_url")} />
          </div>
          <div className="form-field">
            <label className="form-label">Username (userid)</label>
            <input placeholder="PrimeClick username" {...f("sms_user")} />
          </div>
          <div className="form-field">
            <label className="form-label">Password</label>
            <input type="password" placeholder="Leave blank to keep existing" {...f("sms_pass")} />
          </div>
          <div className="form-field">
            <label className="form-label">API Key <span style={{fontSize:11,color:"var(--muted)"}}>(for Balance check — from PrimeClick portal)</span></label>
            <input type="password" placeholder="Leave blank to keep existing" {...f("sms_api_key")} />
          </div>
          <div className="form-field">
            <label className="form-label">Sender ID</label>
            <input placeholder="VOLRES" {...f("sms_sender")} />
          </div>
          <div className="form-field">
            <label className="form-label">Route</label>
            <input placeholder="15" {...f("sms_route")} />
          </div>
          <div className="form-field">
            <label className="form-label">DLT Entity ID</label>
            <input placeholder="TRAI DLT Entity ID (required for India)" {...f("sms_dlt_entity_id")} />
          </div>
        </div>
        <button className="btn" type="submit">Save SMS Settings</button>
      </form>
      <hr style={{ margin: "20px 0", border: "1px solid var(--border)" }} />
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h4 style={{ margin: "0 0 12px" }}>Send Test SMS</h4>
          <div style={{ display: "flex", gap: 8 }}>
            <input placeholder="Phone number (10 digits)" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} style={{ flex: 1 }} />
            <button className="btn secondary" onClick={sendTest} disabled={loading}>{loading ? "Sending…" : "Send Test"}</button>
          </div>
        </div>
        <div className="gw-balance-box">
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 6 }}>SMS Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--navy)" }}>{balance ?? "—"}</div>
          <button className="btn secondary" style={{ marginTop: 8, fontSize: 12 }} onClick={checkBalance}>Check Balance</button>
        </div>
      </div>
    </div>
  );
}

/* ── WhatsApp Settings (icpaas.in — same as vetpl) ──── */
function WhatsAppSettings({ onSaved }) {
  const [form, setForm] = useState({
    wa_api_key: "", wa_version: "v17.0", wa_phone_number_id: "", wa_waba_id: "", wa_sender: "", wa_enabled: "true",
  });
  const [testPhone, setTestPhone] = useState("");
  const [balance, setBalance] = useState(null);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/super-admin/settings/whatsapp").then((r) => setForm((f) => ({ ...f, ...r.data.settings }))).catch(() => {});
  }, []);

  async function save(e) {
    e.preventDefault(); setMsg(""); setErr("");
    try {
      await api.put("/super-admin/settings/whatsapp", form);
      setMsg("WhatsApp settings saved successfully."); onSaved();
    } catch (ex) { setErr(ex.response?.data?.error || "Save failed."); }
  }

  async function sendTest() {
    setMsg(""); setErr(""); setLoading(true);
    try {
      const r = await api.post("/super-admin/settings/test/whatsapp", { phone: testPhone });
      setMsg(r.data.message);
    } catch (ex) { setErr(ex.response?.data?.error || "Test failed."); }
    finally { setLoading(false); }
  }

  async function checkBalance() {
    const r = await api.get("/super-admin/settings/balance/whatsapp").catch(() => ({ data: { balance: "Error" } }));
    setBalance(r.data.balance);
  }

  const f = (k) => ({ value: form[k] ?? "", onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div className="card">
      <div className="gw-card-header">
        <h3 style={{ margin: 0 }}>💬 WhatsApp Settings — icpaas.in</h3>
        <label className="toggle-switch">
          <input type="checkbox" checked={form.wa_enabled !== "false"} onChange={(e) => setForm({ ...form, wa_enabled: e.target.checked ? "true" : "false" })} />
          <span className="toggle-slider" />
        </label>
      </div>
      {msg && <div className="success-text" style={{ marginBottom: 12 }}>{msg}</div>}
      {err && <div className="error-text"   style={{ marginBottom: 12 }}>{err}</div>}
      <form onSubmit={save}>
        <div className="form-grid">
          <div className="form-field" style={{ gridColumn: "1 / -1" }}>
            <label className="form-label">API Key (Bearer Token)</label>
            <input type="password" placeholder="Leave blank to keep existing" {...f("wa_api_key")} />
            <small style={{ color: "var(--muted)", fontSize: 12 }}>Get this from your icpaas.in account</small>
          </div>
          <div className="form-field">
            <label className="form-label">API Version</label>
            <input placeholder="v17.0" {...f("wa_version")} />
          </div>
          <div className="form-field">
            <label className="form-label">Phone Number ID</label>
            <input placeholder="From icpaas.in → Channels" {...f("wa_phone_number_id")} />
          </div>
          <div className="form-field">
            <label className="form-label">WABA ID (Business Account ID)</label>
            <input placeholder="From icpaas.in → Channels" {...f("wa_waba_id")} />
          </div>
          <div className="form-field">
            <label className="form-label">Sender Name (optional)</label>
            <input placeholder="e.g. VETPL" {...f("wa_sender")} />
          </div>
        </div>
        <div className="gw-info-card" style={{ marginBottom: 16 }}>
          Provider: <strong>icpaas.in</strong> — Endpoint: <code>POST https://icpaas.in/{"{version}"}/{"{phoneNumberId}"}/messages</code>
          <br />Auth: <code>Authorization: Bearer &lt;API Key&gt;</code> — Template-based messages only
        </div>
        <button className="btn" type="submit">Save WhatsApp Settings</button>
      </form>
      <hr style={{ margin: "20px 0", border: "1px solid var(--border)" }} />
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h4 style={{ margin: "0 0 8px" }}>Send Test Message</h4>
          <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>Sends a <code>hello_world</code> template message to verify connectivity.</p>
          <div style={{ display: "flex", gap: 8 }}>
            <input placeholder="10-digit mobile number" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} style={{ flex: 1 }} />
            <button className="btn secondary" onClick={sendTest} disabled={loading}>{loading ? "Sending…" : "Send Test"}</button>
          </div>
        </div>
        <div className="gw-balance-box">
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 6 }}>WA Channels</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>{balance ?? "—"}</div>
          <button className="btn secondary" style={{ marginTop: 8, fontSize: 12 }} onClick={checkBalance}>Check Connection</button>
        </div>
      </div>
    </div>
  );
}

/* ── Razorpay Settings ──────────────────────────────── */
function RazorpaySettings({ onSaved }) {
  const [form, setForm] = useState({
    razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "",
    razorpay_mode: "test", razorpay_enabled: "true",
  });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/super-admin/settings/razorpay").then((r) => setForm((f) => ({ ...f, ...r.data.settings }))).catch(() => {});
  }, []);

  async function save(e) {
    e.preventDefault(); setMsg(""); setErr("");
    try {
      await api.put("/super-admin/settings/razorpay", form);
      setMsg("Razorpay settings saved successfully."); onSaved();
    } catch (ex) { setErr(ex.response?.data?.error || "Save failed."); }
  }

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const webhookUrl = `${window.location.origin}/api/payments/webhook`;

  return (
    <div className="card">
      <div className="gw-card-header">
        <h3 style={{ margin: 0 }}>💳 Razorpay Settings</h3>
        <label className="toggle-switch">
          <input type="checkbox" checked={form.razorpay_enabled !== "false"} onChange={(e) => setForm({ ...form, razorpay_enabled: e.target.checked ? "true" : "false" })} />
          <span className="toggle-slider" />
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button
          type="button"
          className={`gw-mode-btn ${form.razorpay_mode === "test" ? "active" : ""}`}
          onClick={() => setForm({ ...form, razorpay_mode: "test" })}
        >🟡 Test Mode</button>
        <button
          type="button"
          className={`gw-mode-btn ${form.razorpay_mode === "live" ? "active-live" : ""}`}
          onClick={() => setForm({ ...form, razorpay_mode: "live" })}
        >🔴 Live Mode</button>
      </div>

      {form.razorpay_mode === "live" && (
        <div className="error-text" style={{ marginBottom: 16, padding: "10px 14px", background: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA" }}>
          ⚠️ <strong>Live mode is active.</strong> Real payments will be processed. Ensure your keys are correct before saving.
        </div>
      )}

      {msg && <div className="success-text" style={{ marginBottom: 12 }}>{msg}</div>}
      {err && <div className="error-text"   style={{ marginBottom: 12 }}>{err}</div>}

      <form onSubmit={save}>
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">Key ID ({form.razorpay_mode === "live" ? "Live" : "Test"}) *</label>
            <input placeholder={`rzp_${form.razorpay_mode}_xxxx`} {...f("razorpay_key_id")} required />
          </div>
          <div className="form-field">
            <label className="form-label">Key Secret</label>
            <input type="password" placeholder="Leave blank to keep existing" {...f("razorpay_key_secret")} />
          </div>
          <div className="form-field">
            <label className="form-label">Webhook Secret</label>
            <input type="password" placeholder="Leave blank to keep existing" {...f("razorpay_webhook_secret")} />
          </div>
        </div>
        <div className="gw-info-card" style={{ marginBottom: 16 }}>
          <div className="gw-info-grid">
            <span className="text-muted" style={{ fontSize: 13 }}>Webhook URL to configure in Razorpay Dashboard:</span>
            <code style={{ fontSize: 12, wordBreak: "break-all" }}>{webhookUrl}</code>
          </div>
        </div>
        <button className="btn" type="submit">Save Razorpay Settings</button>
      </form>
    </div>
  );
}

/* ── Notification Templates ─────────────────────────── */
const NOTIF_TPL_EMPTY = { name: "", channel: "email", purpose: "", subject: "", body: "", is_active: true };
const CHANNEL_COLORS  = { email: "#DBEAFE", sms: "#D1FAE5", whatsapp: "#EDE9FE" };
const CHANNEL_TEXT    = { email: "#1D4ED8", sms: "#065F46", whatsapp: "#6D28D9" };
const VARS_HINT       = "{student_name}, {roll_no}, {class_name}, {exam_name}, {school_name}, {result_link}, {percentage}";

function NotifTemplates() {
  const [templates, setTemplates]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null); // null | "add" | template-object (edit)
  const [form, setForm]             = useState(NOTIF_TPL_EMPTY);
  const [msg, setMsg]               = useState("");
  const [err, setErr]               = useState("");
  const [saving, setSaving]         = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  function load() {
    setLoading(true);
    api.get("/super-admin/notification-templates")
      .then((r) => setTemplates(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function openAdd() {
    setForm(NOTIF_TPL_EMPTY);
    setMsg(""); setErr("");
    setModal("add");
  }

  function openEdit(t) {
    setForm({ name: t.name, channel: t.channel, purpose: t.purpose || "", subject: t.subject || "", body: t.body, is_active: t.is_active });
    setMsg(""); setErr("");
    setModal(t);
  }

  async function save(e) {
    e.preventDefault(); setMsg(""); setErr(""); setSaving(true);
    try {
      if (modal === "add") {
        await api.post("/super-admin/notification-templates", form);
      } else {
        await api.put(`/super-admin/notification-templates/${modal.id}`, form);
      }
      setMsg(modal === "add" ? "Template created." : "Template updated.");
      load();
      setTimeout(() => setModal(null), 900);
    } catch (ex) {
      setErr(ex.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function del(t) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    setDeletingId(t.id);
    try {
      await api.delete(`/super-admin/notification-templates/${t.id}`);
      load();
    } catch (ex) {
      alert(ex.response?.data?.error || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  }

  async function toggleActive(t) {
    try {
      await api.put(`/super-admin/notification-templates/${t.id}`, { is_active: !t.is_active });
      load();
    } catch { /* ignore */ }
  }

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: "0 0 4px" }}>Message Templates</h3>
          <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
            Reusable email, SMS, and WhatsApp message templates with variable placeholders.
          </p>
        </div>
        <button className="btn" onClick={openAdd} style={{ flexShrink: 0 }}>+ Add Template</button>
      </div>

      {loading ? (
        <p className="text-muted">Loading templates…</p>
      ) : templates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✉️</div>
          <div className="empty-state-text">No templates yet. Click "Add Template" to create one.</div>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Channel</th>
                <th>Purpose</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td>
                    <span style={{
                      display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12,
                      fontWeight: 700, background: CHANNEL_COLORS[t.channel] || "#F1F5F9",
                      color: CHANNEL_TEXT[t.channel] || "#475569",
                    }}>
                      {t.channel}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: "var(--muted)" }}>{t.purpose || "—"}</td>
                  <td>
                    <span className={`badge ${t.is_active ? "active" : "suspended"}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleActive(t)}
                      title="Click to toggle"
                    >
                      {t.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn secondary" style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => openEdit(t)}>Edit</button>
                      <button className="btn" style={{ fontSize: 12, padding: "4px 10px", background: "#FEE2E2", color: "#991B1B", border: "1px solid #FECACA" }}
                        onClick={() => del(t)} disabled={deletingId === t.id}>
                        {deletingId === t.id ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="sa-modal-inner" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>{modal === "add" ? "Add Template" : "Edit Template"}</h3>
              <button onClick={() => setModal(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748B" }}>✕</button>
            </div>
            {msg && <div className="success-text" style={{ marginBottom: 12 }}>{msg}</div>}
            {err && <div className="error-text"   style={{ marginBottom: 12 }}>{err}</div>}
            <form onSubmit={save}>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Name *</label>
                  <input placeholder="e.g. Result Published — SMS" {...f("name")} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Channel *</label>
                  <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} required>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Purpose</label>
                  <input placeholder="e.g. result_published, exam_reminder" {...f("purpose")} />
                </div>
                {form.channel === "email" && (
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Subject</label>
                    <input placeholder="Email subject — supports variables" {...f("subject")} />
                  </div>
                )}
                <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Body *</label>
                  <textarea
                    rows={6}
                    placeholder="Message body. Use variables to personalise."
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    required
                    style={{ fontFamily: "monospace", fontSize: 13 }}
                  />
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                    Available variables: <code>{VARS_HINT}</code>
                  </div>
                </div>
                <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                    Active
                  </label>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="btn" type="submit" disabled={saving}>{saving ? "Saving…" : modal === "add" ? "Create Template" : "Save Changes"}</button>
                <button className="btn secondary" type="button" onClick={() => setModal(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Super Admins Tab
   ====================================================== */
function SuperAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add modal
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", username: "", password: "", email: "", mobile: "" });
  const [addMsg, setAddMsg] = useState("");
  const [addError, setAddError] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  // Edit details modal
  const [editAdmin, setEditAdmin] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editMsg, setEditMsg] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Edit credentials modal
  const [credAdmin, setCredAdmin] = useState(null);
  const [credForm, setCredForm] = useState({ username: "", password: "" });
  const [credMsg, setCredMsg] = useState("");
  const [credSaving, setCredSaving] = useState(false);

  function load() {
    setLoading(true);
    api.get("/super-admin/super-admins")
      .then((r) => setAdmins(r.data))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function submitAdd(e) {
    e.preventDefault();
    setAddSaving(true); setAddMsg(""); setAddError("");
    try {
      const r = await api.post("/super-admin/super-admins", addForm);
      setAddMsg(`Super admin "${r.data.name}" created.`);
      setAddForm({ name: "", username: "", password: "", email: "", mobile: "" });
      load();
    } catch (err) {
      setAddError(err.response?.data?.error || "Failed to create.");
    } finally {
      setAddSaving(false);
    }
  }

  function openEdit(a) {
    setEditAdmin(a);
    setEditForm({ name: a.name || "", email: a.email || "", mobile: a.mobile || "" });
    setEditMsg("");
  }

  async function saveEdit() {
    setEditSaving(true); setEditMsg("");
    try {
      await api.put(`/super-admin/super-admins/${editAdmin.id}`, editForm);
      setEditMsg("Details updated.");
      load();
      setTimeout(() => setEditAdmin(null), 800);
    } catch (err) {
      setEditMsg(err.response?.data?.error || "Failed to update.");
    } finally {
      setEditSaving(false);
    }
  }

  function openCred(a) {
    setCredAdmin(a);
    setCredForm({ username: a.username, password: "" });
    setCredMsg("");
  }

  async function saveCred() {
    setCredSaving(true); setCredMsg("");
    try {
      await api.put(`/super-admin/super-admins/${credAdmin.id}/credentials`, credForm);
      setCredMsg("Credentials updated.");
      load();
      setTimeout(() => setCredAdmin(null), 800);
    } catch (err) {
      setCredMsg(err.response?.data?.error || "Failed to update.");
    } finally {
      setCredSaving(false);
    }
  }

  async function toggleStatus(admin) {
    try {
      await api.put(`/super-admin/super-admins/${admin.id}/toggle-status`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed.");
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Super Admin Accounts</h1>
        <p className="subtitle">Manage all super admin users on this platform.</p>
      </div>

      <div className="card">
        <div className="card-header" style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>All Super Admins</h3>
          <button className="btn sm" onClick={() => { setShowAdd(true); setAddMsg(""); setAddError(""); }}>+ Add Super Admin</button>
        </div>

        {loading ? (
          <p className="text-muted">Loading...</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id}>
                  <td><strong>{a.name}</strong></td>
                  <td style={{ fontFamily: "monospace", fontSize: 13 }}>{a.username}</td>
                  <td style={{ fontSize: 13, color: "var(--muted)" }}>{a.email || "—"}</td>
                  <td style={{ fontSize: 13, color: "var(--muted)" }}>{a.mobile || "—"}</td>
                  <td>
                    <span className={`badge ${a.is_active ? "active" : "suspended"}`}>
                      {a.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button className="btn sm secondary" onClick={() => openEdit(a)}>Edit Details</button>
                    <button className="btn sm secondary" onClick={() => openCred(a)}>Edit Credentials</button>
                    <button className={`btn sm ${a.is_active ? "danger" : "success"}`} onClick={() => toggleStatus(a)}>
                      {a.is_active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Super Admin Modal */}
      {showAdd && (
        <div className="tpl-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="tpl-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Add Super Admin</h3>
              <button className="tpl-modal-close" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              <form onSubmit={submitAdd} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>
                  Super admins have full platform access and are required to verify via OTP on every login.
                </p>
                {[
                  { label: "Full Name *", key: "name", required: true },
                  { label: "Username *", key: "username", required: true },
                  { label: "Password * (min 8 chars)", key: "password", type: "password", required: true },
                  { label: "Email (for MFA OTP)", key: "email", type: "email" },
                  { label: "Mobile (for MFA OTP)", key: "mobile" },
                ].map(({ label, key, type, required }) => (
                  <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                    <span>{label}</span>
                    <input
                      type={type || "text"}
                      value={addForm[key]}
                      onChange={(e) => setAddForm({ ...addForm, [key]: e.target.value })}
                      required={required}
                    />
                  </label>
                ))}
                {addMsg && <div className="success-text" style={{ fontSize: 13 }}>{addMsg}</div>}
                {addError && <div className="error-text" style={{ fontSize: 13 }}>{addError}</div>}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button type="button" className="btn secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                  <button type="submit" className="btn" disabled={addSaving}>{addSaving ? "Creating..." : "Create Super Admin"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Details Modal */}
      {editAdmin && (
        <div className="tpl-modal-overlay" onClick={() => setEditAdmin(null)}>
          <div className="tpl-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Edit Details — {editAdmin.username}</h3>
              <button className="tpl-modal-close" onClick={() => setEditAdmin(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { label: "Full Name", key: "name" },
                { label: "Email (MFA OTP destination)", key: "email", type: "email" },
                { label: "Mobile (MFA OTP destination)", key: "mobile" },
              ].map(({ label, key, type }) => (
                <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                  <span>{label}</span>
                  <input type={type || "text"} value={editForm[key] || ""} onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })} />
                </label>
              ))}
              {editMsg && <p style={{ margin: 0, fontSize: 13, color: editMsg.includes("updated") ? "green" : "red" }}>{editMsg}</p>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setEditAdmin(null)}>Cancel</button>
                <button className="btn" onClick={saveEdit} disabled={editSaving}>{editSaving ? "Saving..." : "Save Changes"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Credentials Modal */}
      {credAdmin && (
        <div className="tpl-modal-overlay" onClick={() => setCredAdmin(null)}>
          <div className="tpl-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3 style={{ margin: 0 }}>Edit Credentials — {credAdmin.name}</h3>
              <button className="tpl-modal-close" onClick={() => setCredAdmin(null)}>✕</button>
            </div>
            <div className="tpl-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>Leave password blank to keep it unchanged.</p>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                <span>Username</span>
                <input value={credForm.username} onChange={(e) => setCredForm({ ...credForm, username: e.target.value })} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                <span>New Password (min 8 chars)</span>
                <input type="password" placeholder="Leave blank to keep current" value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })} />
              </label>
              {credMsg && <p style={{ margin: 0, fontSize: 13, color: credMsg.includes("updated") ? "green" : "red" }}>{credMsg}</p>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn secondary" onClick={() => setCredAdmin(null)}>Cancel</button>
                <button className="btn" onClick={saveCred} disabled={credSaving}>{credSaving ? "Saving..." : "Update Credentials"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
